import PropertyDetailsSection from "../PropertyDetailsSection";
import { getItemsByCategories } from "../SectionFeatured.tsx/utils";
import FullPageLoader from "@Components/FullPageLoader";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { saveLastViewedListings } from "@Pages/Search/data/helper";
import { AppState } from "@Store/index";
import { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "hooks/useHistory";
import { useLocation } from "react-router-dom";
import { updatePropertyViewedFlag } from "@App/appReducer";
import setCookie from "helpers/setCookie";
import wrapperHOC from "@Utils/wrapperHOC";
import { registerPostHogSuperProperties, setLastVisitedPropertyForPostHog } from "@Utils/posthog";
import { transformVideoPath } from "../ReviewsSection/data/helper";
import { logAppEvent } from "@Utils/appAnalytics";
import { getPricingCurrencyStandardName } from "../RoomTypeSection/utils";
import { getAvailableStartingPricingHelper } from "@Pages/BookForm/utils";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { SharePropertyModal } from "../PropertyHeader/components/SharePropertyWrapper";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import videoStyles from "@Pages/VideoPage/VideoPage.module.scss";
import { hideLoginModal } from "@Components/Login/loginReducer";
import isWatermarkEnabled from "../GalleryCarousel/isWatermarkEnabled";
// import { useFeatureIsOn } from "@growthbook/growthbook-react";
// import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";

// const GalleryModal = lazy(() => import("../GalleryModal"));
const GalleryCarousel = lazy(() => import("../GalleryCarousel"));
const GalleryV2 = lazy(() => import("../GalleryModal/GalleryV2"));
const VideoPageMobile = lazy(() => import("@Pages/VideoPage/VideoPageMobile"));
const VideoPageDesktop = lazy(() => import("@Pages/VideoPage/VideoPageDesktop"));

const PropertyTopSection = ({
  loading,
  isDesktop = false,
}: {
  loading?: boolean;
  isDesktop?: boolean;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  // const isGalleryNewSection = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.GALLERY_SECTION_V2);

  // to track view content event for reddit pixel
  const inventoryId =
    typeof window !== "undefined" ? window.location.pathname.split("/").pop() : null;
  const hasTrackedViewContent = useRef(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.rdt ||
      !inventoryId ||
      hasTrackedViewContent.current
    )
      return;

    window.rdt("track", "ViewContent", {
      content_type: "property",
      content_id: inventoryId,
    });

    hasTrackedViewContent.current = true;
  }, [inventoryId]);

  const {
    showGalleryModal,
    selectedGalleryTab,
    galleryModalData,
    showGalleryCarousel,
    galleryCarouselData,
    galleryCarouselCurrentIndex,
    galleryCarouselCategory,
    galleryCarouselReviewSection,
    features,
    feature_tags,
    pricing,
    meta_distances,
    meta_facts,
    meta_general_facts,
    meta_community_facts,
    data,
    propertyLocation,
    name,
    destinationDistances,
    redirect_region_name,
    redirect_region_type,
    reviewData,
    showViewRoomButton,
    offers,
    tags,
    regionId,
    property_info_tags,
    fromGallery,
    slashed_available_price,
    slashed_price_tenancy_object,
    showShare,
    include_accreditations,
    showVideoModal,
  } = useSelector(
    ({ propertyPageData }: AppState) => ({
      showGalleryModal: propertyPageData?.gallery_modal?.show,
      selectedGalleryTab: propertyPageData?.gallery_modal?.selectedTab,
      galleryModalData: propertyPageData?.gallery_modal?.data,
      galleryCarouselData: propertyPageData?.gallery_carousel?.images,
      galleryCarouselCurrentIndex: propertyPageData?.gallery_carousel?.currentItemIndex,
      galleryCarouselCategory: propertyPageData?.gallery_carousel?.category,
      galleryCarouselReviewSection: propertyPageData?.gallery_carousel?.reviewSection,
      showGalleryCarousel: propertyPageData?.gallery_carousel?.show,
      showViewRoomButton: propertyPageData?.gallery_carousel?.showViewRoomButton,
      features: propertyPageData?.data?.features,
      feature_tags: propertyPageData?.data?.feature_tags,
      tags: propertyPageData?.data?.tags,
      pricing: propertyPageData?.data?.pricing,
      meta_distances: propertyPageData?.data?.meta_distances,
      meta_facts: propertyPageData?.data?.meta_facts,
      meta_general_facts: propertyPageData?.data?.meta_general_facts,
      meta_community_facts: propertyPageData?.data?.meta_community_facts,
      propertyLocation: propertyPageData?.data?.location,
      name: propertyPageData?.data?.name,
      destinationDistances: propertyPageData?.data?.destination_distances,
      redirect_region_name: propertyPageData?.data?.redirect_region_name,
      redirect_region_type: propertyPageData?.data?.redirect_region_type,
      data: propertyPageData.data,
      reviewData: propertyPageData.reviewData?.result,
      offers: propertyPageData?.data?.active_offers,
      regionId: propertyPageData?.data?.region_id,
      fromGallery: propertyPageData?.gallery_carousel?.fromGallery,
      property_info_tags: propertyPageData?.data?.meta?.property_info_tags,
      slashed_available_price: propertyPageData?.data?.slashed_available_price,
      slashed_price_tenancy_object: propertyPageData?.data?.slashed_price_tenancy_object || null,
      showShare: propertyPageData.showShareModal,
      include_accreditations: propertyPageData?.data?.include_accreditations,
      showVideoModal: propertyPageData?.video_modal?.show,
    }),
    shallowEqual,
  );
  const handleGalleryHideModal = () => {
    propertyPageDispatcher.showGalleryModal({ show: false, selectedTab: "all", data: {} });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("gallery-modal-closed"));
    }
  };
  const propertyDetailsProps = {
    features,
    feature_tags,
    pricing,
    meta_distances,
    meta_facts,
    meta_general_facts,
    meta_community_facts,
    propertyLocation,
    name,
    destinationDistances,
    redirect_region_name,
    redirect_region_type,
    offers,
    tags,
    regionId,
    property_info_tags,
    slashed_available_price,
    slashed_price_tenancy_object,
    include_accreditations,
  };
  const handleCarouselHideModal = () => {
    propertyPageDispatcher.showGalleryCarousel({
      show: false,
      images: [],
      category: "",
      currentItemIndex: 0,
      showViewRoomButton: true,
    });
  };

  // Listen for browser back button to close video modal
  useEffect(() => {
    const handlePopState = () => {
      // If video modal is open and URL no longer contains /video/, close it
      if (showVideoModal && !window.location.pathname.includes("/video/")) {
        propertyPageDispatcher.showVideoModal({ show: false, videoId: "" });
        // Also close any modals that were opened inside the video modal
        // (login, share) since they don't push their own history entries
        propertyPageDispatcher.showShareModal(false);
        dispatch(hideLoginModal());
      }
    };
    if (__CLIENT__) {
      window.addEventListener("popstate", handlePopState);
    }
    return () => {
      if (__CLIENT__) {
        window.removeEventListener("popstate", handlePopState);
      }
    };
  }, [showVideoModal]);

  useEffect(() => {
    if (loading) return;
    logAppEvent({
      eventName: APP_EVENTS.PROPERTY_VIEWED,
      payload: {
        property_id: data?.id,
        property_name: data?.name,
        canonical_name: data?.canonical_name,
        region_canonical_name: data?.region?.canonical_name,
        currency: getPricingCurrencyStandardName(data?.pricing?.currency),
        value: getAvailableStartingPricingHelper(data?.pricing),
        property_location: data?.location?.name,
      },
    });
  }, [loading]);

  // Compute gallery items from property data. Memoized so we can reuse it
  // both in the initial mount effect and in the URL-sync effect.
  const galleryItems = useMemo(() => {
    const filteredReview =
      reviewData?.flatMap(({ media, rating, user_details, anonymous }: any) =>
        (media || []).map(({ path, thumbnail_url }: any) => ({
          path: transformVideoPath(path),
          base_path: transformVideoPath(path),
          platform: "gumlet",
          category: thumbnail_url ? "videos" : "images",
          thumbnail_url: thumbnail_url ? transformVideoPath(thumbnail_url) : null,
          userName: user_details?.name,
          anonymous,
          rating,
          showCaption: true,
        })),
      ) || [];

    const filteredSitePlan =
      data?.site_plan?.map(
        ({ building, number_of_floors, floor_details: [...floor_details_temp] }: any) => ({
          building,
          number_of_floors,
          floor_details: floor_details_temp?.sort?.(
            (a: any, b: any) => parseFloat(a?.floor) - parseFloat(b?.floor),
          ),
        }),
      ) || [];

    const additionData: any = {};
    if (data?.site_plan?.length > 0) {
      additionData["site_plan"] = filteredSitePlan;
    }
    if (filteredReview.length > 0) {
      additionData.shot_by_residents = filteredReview;
    }
    const _galleryItems = { ...getItemsByCategories(data), ...(additionData || {}) };
    const { floor_plan, site_plan, ...items } = _galleryItems;
    if (floor_plan) items["floor_plan"] = floor_plan;
    if (site_plan) items["site_plan"] = site_plan;
    return items;
  }, [data, reviewData]);

  // Initial mount: check URL params and open gallery if coming from direct link / reload.
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    if (params.get("tab")) {
      propertyPageDispatcher.showGalleryModal({
        show: true,
        selectedTab: params.get("tab"),
        data: galleryItems,
      });
    } else {
      propertyPageDispatcher.showGalleryModal({ show: false, selectedTab: "all", data: {} });
    }
  }, []);

  // Record the visit per property, not per mount.
  //
  // `/places/:id` → `/places/:id` (the "similar properties" rail) is an SPA route change that
  // reuses this component — `renderRoutes` puts no param-based key on it — so this used to sit
  // in the mount-only effect above and silently skipped every property after the first. The
  // `property_viewed` analytics event next to it fires on `[loading]`, which is why analytics
  // saw those visits and `INVENTORIES_LAST_VIEWED` did not.
  useEffect(() => {
    if (!data?.canonical_name) return;
    // Slug and name together, both off `data`, so they cannot disagree.
    setLastVisitedPropertyForPostHog(data?.canonical_name, data?.name);
    registerPostHogSuperProperties().catch(() => undefined);
    saveLastViewedListings(data);
    setCookie({
      cname: "propertyViewedFlag",
      cvalue: "true",
      exdays: 365,
    });
    dispatch(updatePropertyViewedFlag(true));
    // `name` is a dependency too: the pair is written together, and `data` can
    // arrive with the canonical first and the name a beat later.
  }, [data?.canonical_name, data?.name]);

  // Sync gallery modal state with URL on forward / back navigation.
  // When the user presses Forward and the URL restores gallery query params,
  // this effect re-opens the gallery. When params disappear (back nav), it's
  // a no-op since useModal's popstate handler already closes it.
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip the first render — the mount effect above handles it.

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const isGalleryUrl = params.get("gallery") === "true" && tab;

    if (isGalleryUrl && !showGalleryModal) {
      propertyPageDispatcher.showGalleryModal({
        show: true,
        selectedTab: tab,
        data: galleryItems,
      });
    }
  }, [location.search]);

  // Prevent forward navigation into an unmounted image carousel.
  // Because 'galleryCarousel' state depends on Redux data (active image, etc.)
  // we can't easily restore it via forward navigation. This listener instantly
  // routes the user back if they navigate forward into a closed carousel,
  // effectively disabling forward navigation for the image modal without
  // breaking the history stack.
  useEffect(() => {
    const blockCarouselForwardNav = () => {
      let currentState: any = {};
      if (typeof window !== "undefined" && window.history?.state) {
        const raw = window.history.state;
        currentState = raw.state && typeof raw.state === "object" ? raw.state : raw;
      }
      const modals = Array.isArray(currentState?._modals) ? currentState._modals : [];
      if (modals.includes("galleryCarousel") && !showGalleryCarousel) {
        window.history.go(-1);
      }
    };
    window.addEventListener("popstate", blockCarouselForwardNav);
    return () => window.removeEventListener("popstate", blockCarouselForwardNav);
  }, [showGalleryCarousel]);

  return (
    <>
      <PropertyDetailsSection
        isLoading={loading || false}
        isDesktop={isDesktop}
        {...propertyDetailsProps}
        {...data}
      />
      {showGalleryModal && (
        <Portal rootId="forPortal">
          <Modal
            type="galleryModal"
            hideModal={handleGalleryHideModal}
            size="fullScreen"
            animation="bottomToTop"
            isHistoryPush
            queryParams={`gallery=true&galleryId=${data.id}&tab=${selectedGalleryTab}`}
            disableEscapeKey={showVideoModal}
          >
            <Suspense fallback={<FullPageLoader />}>
              <GalleryV2
                currentTab={selectedGalleryTab}
                galleryItems={galleryModalData}
                isDesktop={isDesktop}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
      {showGalleryCarousel && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={handleCarouselHideModal}
            size="fullScreen"
            animation="bottomToTop"
            hideCloseButton
            zIndex={1200}
            type="galleryCarousel"
            isHistoryPush
            noLockBody={fromGallery}
          >
            <Suspense fallback={<FullPageLoader />}>
              <GalleryCarousel
                currentCategoryItems={galleryCarouselData}
                initialItemIndex={galleryCarouselCurrentIndex}
                currentCategory={galleryCarouselCategory}
                showViewRoomButton={showViewRoomButton}
                galleryCarouselReviewSection={galleryCarouselReviewSection}
                isDesktop={isDesktop}
                withWatermark={isWatermarkEnabled(data?.meta)}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
      {showShare && !showVideoModal && <SharePropertyModal />}
      {showVideoModal && (
        <Portal rootId="forPortal">
          <div
            id="video-modal"
            className={formatClassNames(
              videoStyles.videoModalOverlay,
              isDesktop && videoStyles.videoModalOverlayDesktop,
            )}
          >
            <Suspense fallback={<FullPageLoader />}>
              {isDesktop ? <VideoPageDesktop /> : <VideoPageMobile />}
            </Suspense>
            {showShare && <SharePropertyModal noHistoryPush />}
          </div>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(PropertyTopSection, {
  componentName: "PropertyTopSection-PropertyPage",
  showForChina: true,
});
